/**
 * Settings Context
 * src/context/SettingsContext.jsx
 *
 * Provides language (es/en) and exchange rates (USD→MXN, EUR→MXN) to the whole app.
 */

import { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const TRANSLATIONS = {
  es: {
    // Nav labels
    dashboard: 'Dashboard', pagos: 'Pagos', patrimonio: 'Patrimonio',
    ingresos: 'Ingresos', gastos: 'Gastos', proyeccion: 'Proyección',
    fondos: 'Fondos', vacaciones: 'Vacaciones', metas: 'Metas',
    catalogos: 'Catálogos', historial: 'Historial', settings: 'Configuración',
    // Common
    save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar', add: 'Agregar',
    edit: 'Editar', all: 'Todos', loading: 'Cargando...', close: 'Cerrar',
    // Person
    person: 'Persona',
    // Pagos
    payments: 'Pagos', paid: 'Pagado', pending: 'Pendiente',
    budget: 'Presupuesto', amount: 'Monto', total: 'Total',
    paidBy: 'Pagado por', editedBy: 'Editado por',
    addExtraPayment: '+ Pago Extra',
    extraPaymentTitle: 'Pago No Presupuestado',
    category: 'Categoría', essential: 'Esencial', payee: '¿A quién?',
    currency: 'Moneda', exchangeRate: 'Tipo de cambio',
    markAsPaid: 'Marcar como pagado',
    // Gastos
    expenses: 'Gastos', medioPago: 'Medio de Pago',
    // Ingresos
    income: 'Ingresos', tipo: 'Tipo', bruto: 'Bruto',
    impuestos: 'Impuestos', neto: 'Neto',
    // Fondos
    funds: 'Fondos de Inversión', invested: 'Invertido',
    projected: 'Proyectado', gain: 'Ganancia',
    rate: 'Tasa', months: 'Meses', investDate: 'Fecha Inversión',
    // Vacaciones
    timeshare: 'Time Share', deposit: 'Depósito',
    monthlyPayment: 'Pago Mensual', paidMonths: 'Meses Pagados',
    remainingMonths: 'Meses Por Pagar', balance: 'Balance', totalValue: 'Valor Total',
    purchaseDate: 'Fecha Compra',
    // Settings
    settingsTitle: 'Configuración', language: 'Idioma', exchangeRates: 'Tipos de Cambio',
    fetchAutomatic: 'Obtener Automático', rateUpdated: 'Tipos de cambio actualizados',
    settingsSaved: 'Configuración guardada',
  },
  en: {
    dashboard: 'Dashboard', pagos: 'Payments', patrimonio: 'Net Worth',
    ingresos: 'Income', gastos: 'Expenses', proyeccion: 'Projection',
    fondos: 'Investments', vacaciones: 'Vacations', metas: 'Goals',
    catalogos: 'Catalogs', historial: 'History', settings: 'Settings',
    save: 'Save', cancel: 'Cancel', delete: 'Delete', add: 'Add',
    edit: 'Edit', all: 'All', loading: 'Loading...', close: 'Close',
    person: 'Person',
    payments: 'Payments', paid: 'Paid', pending: 'Pending',
    budget: 'Budget', amount: 'Amount', total: 'Total',
    paidBy: 'Paid by', editedBy: 'Edited by',
    addExtraPayment: '+ Extra Payment',
    extraPaymentTitle: 'Unbudgeted Payment',
    category: 'Category', essential: 'Essential', payee: 'Payee / Store',
    currency: 'Currency', exchangeRate: 'Exchange Rate',
    markAsPaid: 'Mark as paid',
    expenses: 'Expenses', medioPago: 'Payment Method',
    income: 'Income', tipo: 'Type', bruto: 'Gross',
    impuestos: 'Taxes', neto: 'Net',
    funds: 'Investment Funds', invested: 'Invested',
    projected: 'Projected', gain: 'Gain',
    rate: 'Rate', months: 'Months', investDate: 'Investment Date',
    timeshare: 'Time Share', deposit: 'Deposit',
    monthlyPayment: 'Monthly Payment', paidMonths: 'Paid Months',
    remainingMonths: 'Remaining Months', balance: 'Balance', totalValue: 'Total Value',
    purchaseDate: 'Purchase Date',
    settingsTitle: 'Settings', language: 'Language', exchangeRates: 'Exchange Rates',
    fetchAutomatic: 'Fetch Automatically', rateUpdated: 'Exchange rates updated',
    settingsSaved: 'Settings saved',
  },
};

const SettingsContext = createContext(null);

export function SettingsProvider({ db, children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'es');
  const [rates, setRates] = useState({ USD: 20, EUR: 22 });

  useEffect(() => {
    if (!db) return;
    getDoc(doc(db, 'settings', 'general')).then(snap => {
      if (snap.exists()) {
        const d = snap.data();
        if (d.rates) setRates(d.rates);
        if (d.lang) { setLang(d.lang); localStorage.setItem('lang', d.lang); }
      }
    }).catch(() => {});
  }, [db]);

  const t = (key) => TRANSLATIONS[lang]?.[key] || TRANSLATIONS['es']?.[key] || key;

  const updateLang = async (newLang) => {
    setLang(newLang);
    localStorage.setItem('lang', newLang);
    if (db) {
      try { await setDoc(doc(db, 'settings', 'general'), { lang: newLang }, { merge: true }); }
      catch (err) { console.warn(err); }
    }
  };

  const updateRates = async (newRates) => {
    setRates(newRates);
    if (db) {
      try { await setDoc(doc(db, 'settings', 'general'), { rates: newRates }, { merge: true }); }
      catch (err) { console.warn(err); }
    }
  };

  return (
    <SettingsContext.Provider value={{ lang, rates, t, updateLang, updateRates }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
